import React, { createContext, memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { moduleSettings } from "../../../stores/module-settings.store";
import { Title } from "../../title/title";
import { VoucherInput } from "../../voucher/voucher-input";
import { PendingBooking, Voucher } from "../../../types";
import { pendingBookingStore } from "../../../stores/pending-booking.store";
import { VoucherItem } from "../../voucher/voucher-item";
import { removeVoucherCodeToPendingBooking } from "../../../requests/pending-booking.requests";
import { useDetailsVoucherContext } from "../../../contexts/commercial-context";
import { ValidationErrorApiErrorImpl } from "../../../services/api-errors/api-errors-impl";
import { useErrorHandler } from "react-error-boundary";
import { ApiErrorCode } from "../../../services/api-errors";

interface IStyle {
  voucherAllowed: boolean;
}

const DiscountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;
`;

const VouchersWrapper = styled.div`
  width: fit-content;
  background-color: ${(props) => props.theme.backgroundTransparentWhite};
  border-radius: 0 8px 8px 8px;
  padding: 4px 4px 0 4px;
`;

const VoucherInputWrapper = styled.div<IStyle>`
  opacity: ${(props) => (props.voucherAllowed ? 1 : 0.4)};
  pointer-events: ${(props) => (props.voucherAllowed ? "auto" : "none")};
`;

const VoucherItems = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const MessageInfo = styled.div`
  font-size: 0.8rem;
  margin-bottom: 8px;
  padding: 4px;
  width: 300px;
`;

const MessageError = styled(MessageInfo)`
  color: #e74c3c;
`;

interface VoucherInputInterface {
  updateMessageError: (errorMsg?: string) => void;
  updateVouchers: (vouchers: Array<Voucher>) => void;
  voucherAllowed: boolean;
  messageError?: string;
}
export const VoucherInputCtx = createContext<VoucherInputInterface>(
  {} as VoucherInputInterface
);
const Vouchers = () => {
  const { t } = useTranslation();

  const handleCanDisplayInput = (vouchers: Array<Voucher>) => {
    return multiVouchersEnabled ? true : vouchers && vouchers.length < 1;
  };

  const multiVouchersEnabled = moduleSettings.multiVouchersEnabled;
  const [voucherAllowed, setVoucherAllowed] = useState(
    pendingBookingStore.getIsVoucherAllowed()
  );
  const [vouchers, setVouchers] = useState<Array<Voucher>>(
    pendingBookingStore.selectedVouchers
  );
  const [voucherToRemove, setVoucherToRemove] = useState("");
  const [messageError, setMessageError] = useState<string | undefined>(
    undefined
  );
  const messageInfo = t("instabook.gift_code.voucher_not_allowed");
  const [canDisplayInput, setCanDisplayInput] = useState(
    handleCanDisplayInput(vouchers)
  );
  const { updateValidVoucherCode } = useDetailsVoucherContext();

  const handleRemoveVoucher = (value: string): void => {
    setVoucherToRemove(value);
  };

  const handleAddVoucher = (updatedVouchers: Array<Voucher>) => {
    setVouchers(updatedVouchers);
    setCanDisplayInput(handleCanDisplayInput(updatedVouchers));
  };
  const throwError = useErrorHandler();

  useEffect(() => {
    if (voucherToRemove) {
      const subscription = removeVoucherCodeToPendingBooking(
        voucherToRemove
      ).subscribe({
        next: (resp: PendingBooking) => {
          pendingBookingStore.pendingBooking = resp;
          setVouchers(resp.vouchers);
          setVoucherToRemove("");
          setCanDisplayInput(handleCanDisplayInput(resp.vouchers));
          setVoucherAllowed(pendingBookingStore.getIsVoucherAllowed());
          updateValidVoucherCode(resp.vouchers.length > 0);
        },
        error: (error) => {
          if (error instanceof ValidationErrorApiErrorImpl) {
            const _getVoucherDetailsError = error.getVoucherDetailsError();
            if (_getVoucherDetailsError !== undefined) {
              setMessageError(
                error.response.data["error"][
                  _getVoucherDetailsError!.toString()
                ][0]
              );
            }
          } else {
            throwError(error);
          }
        },
      });
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [voucherToRemove]);

  const renderVouchers = vouchers?.map((voucher: Voucher) => {
    return (
      <VoucherItem
        key={voucher.code}
        voucher={voucher}
        onRemoveVoucher={handleRemoveVoucher}
        isRemovable={true}
      />
    );
  });

  return (
    <DiscountWrapper data-test="voucher-detail">
      <Title
        text={t("instabook.gift_code.enter_your_discount_code")}
        type="tab"
      />
      <VouchersWrapper>
        <VoucherInputCtx.Provider
          value={{
            updateMessageError: (errorMsg?: string) =>
              setMessageError(errorMsg),
            updateVouchers: (updatedVouchers: Array<Voucher>) =>
              handleAddVoucher(updatedVouchers),
            voucherAllowed,
            messageError,
          }}
        >
          <VoucherInputWrapper
            data-test="voucher-wrapper"
            voucherAllowed={voucherAllowed}
          >
            {canDisplayInput && <VoucherInput />}
          </VoucherInputWrapper>{" "}
        </VoucherInputCtx.Provider>
        {messageError && (
          <MessageError data-test="message-error">{messageError}</MessageError>
        )}
        {!voucherAllowed && (
          <MessageInfo data-test="message-info">{messageInfo}</MessageInfo>
        )}
        <VoucherItems>{renderVouchers}</VoucherItems>
      </VouchersWrapper>
    </DiscountWrapper>
  );
};

export const DetailsVouchers = memo(Vouchers);
